import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useArticles } from '../contexts/ArticleContext';
import { Article } from '../types';
import SEO from '../components/SEO';
import { trackArticleView } from '../utils/analytics';
import { useArticleCache } from '../hooks/useArticleCache';

const PageWrapper = styled.div`
  background-color: ${theme.colors.primary};
  min-height: 100vh;
  padding: ${theme.space.xl} ${theme.space.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.space.md} ${theme.space.xs};
  }
`;

const ArticleContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background-color: white;
  border-radius: ${theme.radii.lg};
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 95%;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 100%;
    border-radius: ${theme.radii.md};
  }
`;

const ArticleHeader = styled.div`
  padding: ${theme.space.lg} ${theme.space.lg} 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.space.md} ${theme.space.md} 0;
  }
`;

const ArticleDate = styled.div`
  font-family: ${theme.fonts.body};
  color: ${theme.colors.lightText};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: ${theme.space.sm};
`;

const ArticleTitle = styled.h1`
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes['2xl']};
  margin-bottom: ${theme.space.lg};
  line-height: 1.4;
  font-weight: bold;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.xl};
    margin-bottom: ${theme.space.md};
  }
`;

const ArticleMeta = styled.div`
  font-family: ${theme.fonts.body};
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.space.sm};
  margin-bottom: ${theme.space.lg};
`;

const CategoryContainer = styled.div`
  display: flex;
  gap: ${theme.space.xs};
  align-items: center;
  flex-wrap: wrap;
`;

const ArticleCategory = styled.span`
  font-family: ${theme.fonts.body};
  background-color: #e3f2fd;
  color: #1976d2;
  padding: ${theme.space.xs} ${theme.space.sm};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.sm};
  display: inline-block;
`;

const ArticleSubCategory = styled(ArticleCategory)`
  background-color: #f3e5f5;
  color: #7b1fa2;
`;

const ArticleImage = styled.div<{ src?: string }>`
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 のアスペクト比 (9/16 = 0.5625 = 56.25%) */
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin-bottom: ${theme.space.lg};
  position: relative;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-bottom: ${theme.space.md};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.space.md};
  }
`;

const TableOfContents = styled.div`
  font-family: ${theme.fonts.body};
  background-color: #f5f5f5;
  padding: ${theme.space.md};
  margin: 0 ${theme.space.lg} ${theme.space.lg};
  border-radius: ${theme.radii.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: 0 ${theme.space.md} ${theme.space.md};
    padding: ${theme.space.sm};
  }
`;

const TocTitle = styled.h2`
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.space.sm};
  font-weight: bold;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.md};
  }
`;

const TocList = styled.ol`
  font-family: ${theme.fonts.body};
  list-style-position: inside;
  list-style-type: decimal;
  padding-left: ${theme.space.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-left: ${theme.space.md};
  }
  
  li {
    margin-bottom: ${theme.space.xs};
    padding-bottom: ${theme.space.sm};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  a {
    color: ${theme.colors.text};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .toc-h1-link {
    text-decoration: underline;
    text-decoration-color: #e0e0e0;
    text-decoration-thickness: 3px;
    text-underline-offset: 4px;
    text-decoration-skip-ink: none;
  }
`;

const TocH2List = styled.div`
  font-family: ${theme.fonts.body};
  list-style-type: none;
  margin-top: ${theme.space.xs};
  margin-left: ${theme.space.md};
  
  a {
    color: ${theme.colors.text};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TocH3Container = styled.div`
  font-family: ${theme.fonts.body};
  margin-top: ${theme.space.xs};
  margin-bottom: ${theme.space.sm};
  list-style-type: none;
  padding-bottom: ${theme.space.xs};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const TocH3Item = styled.div`
  font-family: ${theme.fonts.body};
  margin: ${theme.space.xs} 0;
  
  a {
    color: ${theme.colors.text};
    font-size: ${theme.fontSizes.sm};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ArticleContent = styled.div`
  font-family: ${theme.fonts.body};
  font-size: ${theme.fontSizes.md};
  line-height: ${theme.lineHeights.normal};
  padding: 0 ${theme.space.lg} ${theme.space.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.space.md} ${theme.space.lg};
  }
  
  h1, h2, h3 {
    scroll-margin-top: 80px;
  }
  
  h1 {
    position: relative;
    font-size: ${theme.fontSizes.xl};
    background-color: #f7f9fa;
    padding: 0.2em ${theme.space.md};
    border-left: 5px solid transparent;
    border-radius: 3px;
  }

  h1::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 5px;
    height: 1.2em;
    background-color: ${theme.colors.primary};
    border-radius: 2px;
  }
  
  h2 {
    font-size: ${theme.fontSizes.xl};
    border-bottom: 3px solid #cccccc;
    padding-bottom: ${theme.space.xs};
    margin-bottom: ${theme.space.md};
    background-color: transparent;
  }
    
  p {
    margin-bottom: ${theme.space.sm};
    white-space: pre-line;
  }
  
  img {
    max-width: 100%;
    height: auto;
    margin: 0 0;
    border-radius: ${theme.radii.sm};
    /* 縦長画像の最大高さを制限 */
    max-height: 400px;
    object-fit: contain;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      max-height: 400px;
    }
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      max-height: 300px;
    }
  }
  
  .youtube-embed {
    height: 500px;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      height: 350px;
    }

    @media (max-width: ${theme.breakpoints.mobile}) {
      height: 200px;
    }
  }
  
  .youtube-embed iframe {
    position: static;
    display: block;
    border-radius: ${theme.radii.sm};
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    height: 500px;

    @media (max-width: ${theme.breakpoints.tablet}) {
      height: 350px;
    }

    @media (max-width: ${theme.breakpoints.mobile}) {
      height: 200px;
    }
  }
  
  figure {
    margin: ${theme.space.md} 0;
    padding: 0;
    width: 100%;
  }
  
  figure.article-image {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: ${theme.space.lg} auto;
  }
  
  figure.article-image img {
    max-width: 100%;
    height: auto;
    margin: 0;
    /* figure内の画像も同様に最大高さを制限 */
    max-height: 600px;
    object-fit: contain;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      max-height: 500px;
    }
    
    @media (max-width: ${theme.breakpoints.mobile}) {
      max-height: 400px;
    }
  }
  
  figcaption.image-caption {
    font-family: ${theme.fonts.body};
    margin-top: ${theme.space.xs};
    font-size: ${theme.fontSizes.sm};
    color: ${theme.colors.lightText};
    text-align: center;
    font-style: italic;
    max-width: 90%;
  }

  h1 {
    font-family: ${theme.fonts.heading};
    font-size: ${theme.fontSizes['3xl']};
    font-weight: bold;
    margin: ${theme.space.xl} 0 ${theme.space.md};
  }

  h2 {
    font-family: ${theme.fonts.heading};
    font-size: ${theme.fontSizes.xl};
    font-weight: bold;
    margin: ${theme.space.xl} 0 ${theme.space.md};
  }
  
  h3 {
    font-family: ${theme.fonts.heading};
    font-size: ${theme.fontSizes.lg};
    font-weight: bold;
    margin: ${theme.space.lg} 0 ${theme.space.md};
  }
  
  ul, ol {
    margin: ${theme.space.md} 0;
    padding-left: ${theme.space.lg};
  }
  
  blockquote {
    border-left: 4px solid ${theme.colors.primary};
    padding-left: ${theme.space.md};
    margin: ${theme.space.md} 0;
    font-style: italic;
    color: ${theme.colors.lightText};
  }
  
  a {
    color: ${theme.colors.text};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
    
    &[href^="https://"] {
      text-decoration: underline;
      text-decoration-color: #aaaaaa;
      text-decoration-thickness: 1px;
      text-underline-offset: 2px;
      color: #aaaaaa;
    }
  }
  
  .article-link-card {
    margin: ${theme.space.md} 0;
    border: 1px solid #e0e0e0;
    border-radius: ${theme.radii.md};
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: transform 0.2s, box-shadow 0.2s;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    .article-link {
      display: flex;
      text-decoration: none;
      color: ${theme.colors.text};
      
      &:hover {
        text-decoration: none;
      }
      
      @media (max-width: ${theme.breakpoints.mobile}) {
        flex-direction: column;
      }
    }
    
    .article-link-image {
      flex: 0 0 120px;
      background-color: #f5f5f5;
      display: flex;
      align-items: center;
      justify-content: center;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        margin: 0;
      }
      
      @media (max-width: ${theme.breakpoints.mobile}) {
        flex: 0 0 150px;
      }
    }
    
    .article-link-content {
      flex: 1;
      padding: ${theme.space.sm};
      display: flex;
      flex-direction: column;
    }
    
    .article-link-title {
      font-weight: bold;
      margin-bottom: ${theme.space.xs};
      font-size: ${theme.fontSizes.md};
    }
    
    .article-link-excerpt {
      font-size: ${theme.fontSizes.sm};
      color: ${theme.colors.lightText};
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
  
  /* サムネイルなし記事カード用のスタイル */
  .article-link-card.article-link-no-thumbnail {
    .article-link {
      display: block;
    }
    
    .article-link-content {
      padding: ${theme.space.md};
    }
    
    .article-link-title {
      font-size: ${theme.fontSizes.lg};
      margin-bottom: ${theme.space.sm};
    }
    
    .article-link-excerpt {
      font-size: ${theme.fontSizes.md};
      line-height: 1.5;
      -webkit-line-clamp: 3;
    }
  }
  
  /* 単一行の改行のスタイル */
  .single-line-break {
    height: ${theme.space.sm};
    display: block;
    content: '';
  }
  
  /* 注意書きボックスのスタイル */
  .caution-box {
    background-color: #e3f2fd;
    border: 1px solid #90caf9;
    border-radius: ${theme.radii.md};
    padding: ${theme.space.md};
    margin: ${theme.space.md} 0;
    
    .caution-content {
      color: ${theme.colors.text};
      font-size: ${theme.fontSizes.md};
      line-height: 1.6;
      font-weight: normal;
    }
  }
`;

const HomeButton = styled(Link)`
  background-color: #D67616;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 30px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s;
  display: block;
  margin: ${theme.space.xl} auto 0;
  text-align: center;
  width: fit-content;
  text-decoration: none;
  
  &:hover {
    background-color: #e55a00;
  }
`;

const MascotWrapper = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 95%;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 100%;
    padding: 0 ${theme.space.xs};
  }
`;

const Mascot = styled.div`
  position: absolute;
  bottom: -60px;
  right: 0px;
  width: 100px;
  height: 100px;
  background: url('/images/arayottokun.svg') no-repeat;
  background-size: contain;
  z-index: 5;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80px;
    height: 80px;
    bottom: -50px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 60px;
    height: 60px;
    bottom: -40px;
    right: 10px;
  }
`;

const NotFound = styled.div`
  text-align: center;
  padding: ${theme.space.xl};
  background-color: white;
  border-radius: ${theme.radii.lg};
  
  h2 {
    font-size: ${theme.fontSizes['2xl']};
    margin-bottom: ${theme.space.md};
  }
  
  p {
    color: ${theme.colors.lightText};
    margin-bottom: ${theme.space.lg};
  }
`;

const Loading = styled.div`
  text-align: center;
  padding: ${theme.space.xl};
  background-color: white;
  border-radius: ${theme.radii.lg};
  
  h2 {
    font-size: ${theme.fontSizes['xl']};
    margin-bottom: ${theme.space.md};
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  padding: ${theme.space.md};
  margin: ${theme.space.md} 0;
  border: 1px solid ${theme.colors.error};
  border-radius: ${theme.radii.md};
  background-color: white;
`;

const CountriesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.space.xs};
`;

const CountryTag = styled(Link)`
  font-family: ${theme.fonts.body};
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: ${theme.space.xs} ${theme.space.sm};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.sm};
  text-decoration: none;
  display: inline-block;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #c8e6c9;
    color: #1b5e20;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

// ユーティリティ関数
const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

const processArticleLinks = (content: string, articles: Article[]): string => {
  if (!content) return '';
  
  // 通常の記事間リンクのパターン `article/{{slug}}` を検出
  const linkPattern = /`article\/([^`]+)`/g;
  
  // サムネイルなしの記事間リンクのパターン `article-text/{{slug}}` を検出
  const noThumbnailLinkPattern = /`article-text\/([^`]+)`/g;
  
  // 注意書きボックスのパターン `caution/{{content}}` を検出
  const cautionPattern = /`caution\/([^`]+)`/g;
  
  // まず注意書きパターンを処理
  let processedContent = content.replace(cautionPattern, (match, cautionContent) => {
    // 注意書きボックス用のHTMLを生成
    return `<div class="caution-box">
      <div class="caution-content">${cautionContent}</div>
    </div>`;
  });
  
  // 次にサムネイルなしパターンを処理
  processedContent = processedContent.replace(noThumbnailLinkPattern, (match, linkSlug) => {
    // リンク先の記事を探す
    const linkedArticle = articles.find(a => a.slug === linkSlug);
    
    if (!linkedArticle) {
      // 記事が見つからない場合は「見つからない旨」を表示するリンクカード（サムネイルなし）
      return `<div class="article-link-card article-link-no-thumbnail" data-slug="${linkSlug}">
        <div class="article-link" style="cursor: default; opacity: 0.7;">
          <div class="article-link-content">
            <div class="article-link-title">記事が見つかりません</div>
            <div class="article-link-excerpt">リンク先の記事「${linkSlug}」は現在利用できません。</div>
          </div>
        </div>
      </div>`;
    }
    
    // サムネイルなしリンクカード用のHTMLを生成
    return `<div class="article-link-card article-link-no-thumbnail" data-slug="${linkSlug}">
      <a href="/article/${linkSlug}" class="article-link">
        <div class="article-link-content">
          <div class="article-link-title">${linkedArticle.title}</div>
          <div class="article-link-excerpt">${linkedArticle.excerpt || ''}</div>
        </div>
      </a>
    </div>`;
  });
  
  // 次に通常の（サムネイル付き）パターンを処理
  return processedContent.replace(linkPattern, (match, linkSlug) => {
    // リンク先の記事を探す
    const linkedArticle = articles.find(a => a.slug === linkSlug);
    
    if (!linkedArticle) {
      // 記事が見つからない場合は「見つからない旨」を表示するリンクカード
      return `<div class="article-link-card" data-slug="${linkSlug}">
        <div class="article-link" style="cursor: default; opacity: 0.7;">
          <div class="article-link-image" style="background-color: #f9f9f9; justify-content: center; align-items: center;">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12Z" fill="#cccccc"/>
              <path d="M12 10C11.4477 10 11 10.4477 11 11V17C11 17.5523 11.4477 18 12 18C12.5523 18 13 17.5523 13 17V11C13 10.4477 12.5523 10 12 10Z" fill="#cccccc"/>
              <path d="M12 6C11.4477 6 11 6.44772 11 7C11 7.55228 11.4477 8 12 8H12.01C12.5623 8 13.01 7.55228 13.01 7C13.01 6.44772 12.5623 6 12.01 6H12Z" fill="#cccccc"/>
            </svg>
          </div>
          <div class="article-link-content">
            <div class="article-link-title">記事が見つかりません</div>
            <div class="article-link-excerpt">リンク先の記事「${linkSlug}」は現在利用できません。</div>
          </div>
        </div>
      </div>`;
    }
    
    // リンクカード用のHTMLを生成
    return `<div class="article-link-card" data-slug="${linkSlug}">
      <a href="/article/${linkSlug}" class="article-link">
        <div class="article-link-image">
          ${linkedArticle.thumbnail ? `<img src="${linkedArticle.thumbnail}" alt="${linkedArticle.title}">` : ''}
        </div>
        <div class="article-link-content">
          <div class="article-link-title">${linkedArticle.title}</div>
          <div class="article-link-excerpt">${linkedArticle.excerpt || ''}</div>
        </div>
      </a>
    </div>`;
  });
};

// YouTubeのURLを埋め込みカードに変換する関数
const processYoutubeLinks = (content: string): string => {
  if (!content) return '';
  
  // すでにYouTube iframeが存在する箇所を特定するための正規表現
  const iframeRegex = /<iframe[^>]*youtube\.com\/embed\/[^>]*><\/iframe>/g;
  
  // iframeのある部分を一時的にプレースホルダーに置き換え
  const iframes: string[] = [];
  let tempContent = content.replace(iframeRegex, (match) => {
    iframes.push(match);
    return `__YOUTUBE_IFRAME_${iframes.length - 1}__`;
  });
  
  // YouTube APIスタイルのHTMLリンクを検出する正規表現
  // 例: <a class="ytmVideoInfoVideoTitle" href="http://www.youtube.com/watch?v=VIDEO_ID">...</a>
  const ytApiLinkRegex = /<a[^>]*href="https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})[^"]*"[^>]*>.*?<\/a>/g;
  
  // YouTube APIスタイルのリンクを埋め込みカードに置換
  tempContent = tempContent.replace(ytApiLinkRegex, (match, videoId) => {
    return `<div class="youtube-embed">
      <iframe 
        width="100%" 
        height="100%"
        src="https://www.youtube.com/embed/${videoId}" 
        title="YouTube video" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen
        class="youtube-iframe"
      ></iframe>
    </div>`;
  });
  
  // YouTube URLのパターンを検出（標準URLとショートURLの両方に対応、複数のパラメータにも対応）
  // youtube.com/watch?v=VIDEOID&param=value... 形式
  const youtubeWatchRegex = /https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:[^&]+&)*v=([a-zA-Z0-9_-]{11})(?:&[^&]+)*/g;
  // youtu.be/VIDEOID 形式
  const youtuBeRegex = /https?:\/\/(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/g;
  
  // YouTube標準URLを埋め込みカードに置換
  tempContent = tempContent.replace(youtubeWatchRegex, (match, videoId) => {
    return `<div class="youtube-embed">
      <iframe 
        width="100%" 
        height="100%"
        src="https://www.youtube.com/embed/${videoId}" 
        title="YouTube video" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen
        class="youtube-iframe"
      ></iframe>
    </div>`;
  });
  
  // YouTubeショートURLを埋め込みカードに置換
  tempContent = tempContent.replace(youtuBeRegex, (match, videoId) => {
    return `<div class="youtube-embed">
      <iframe 
        width="100%" 
        height="100%" 
        src="https://www.youtube.com/embed/${videoId}" 
        title="YouTube video" 
        frameborder="0" 
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
        allowfullscreen
        class="youtube-iframe"
      ></iframe>
    </div>`;
  });
  
  // 保存しておいたiframeを元に戻す
  iframes.forEach((iframe, index) => {
    tempContent = tempContent.replace(`__YOUTUBE_IFRAME_${index}__`, iframe);
  });
  
  return tempContent;
};

const addHeadingIds = (content: string): string => {
  let h1Count = 0;
  let h2Count = 0;
  let h3Count = 0;
  
  // h1タグの前にアンカーを追加
  let processedContent = content.replace(/<h1[^>]*>(.*?)<\/h1>/g, (match, headingText) => {
    const id = `heading-h1-${h1Count}`;
    h1Count++;
    return `<h1 id="${id}">${headingText}</h1>`;
  });
  
  // h2タグの前にアンカーを追加
  processedContent = processedContent.replace(/<h2[^>]*>(.*?)<\/h2>/g, (match, headingText) => {
    const id = `heading-h2-${h2Count}`;
    h2Count++;
    return `<h2 id="${id}">${headingText}</h2>`;
  });
  
  // h3タグの前にアンカーを追加
  processedContent = processedContent.replace(/<h3[^>]*>(.*?)<\/h3>/g, (match, headingText) => {
    const id = `heading-h3-${h3Count}`;
    h3Count++;
    return `<h3 id="${id}">${headingText}</h3>`;
  });
  
  return processedContent;
};

const processLineBreaks = (content: string): string => {
  if (!content) return '';
  
  // <p></p>間の単一の空行をカスタムクラスに置き換え
  return content.replace(/<\/p>\s*<p>/g, (match) => {
    if (match.includes('\n\n')) {
      // 2行以上の改行はそのまま
      return match;
    } else if (match.includes('\n')) {
      // 1行の改行は特別なスタイルを適用
      return '</p><span class="single-line-break"></span><p>';
    }
    return match;
  });
};

// コンポーネント
const ArticleDateDisplay: React.FC<{ date: string; created_at?: string }> = ({ date, created_at }) => (
  <StyledArticleDate>
    {new Date(created_at || date).toLocaleDateString('ja-JP')}
  </StyledArticleDate>
);

const ArticleCategories: React.FC<{ category?: string; subCategory?: string[] }> = ({ 
  category, 
  subCategory 
}) => (
  <CategoryContainer>
    {category && (
      <StyledArticleCategory to={`/category/${category}`}>
        {category}
      </StyledArticleCategory>
    )}
    {subCategory && subCategory.length > 0 && 
      subCategory.map((sub, index) => (
        <StyledArticleSubCategory 
          key={index} 
          to={`/category/${category}/${sub}`}
        >
          {sub}
        </StyledArticleSubCategory>
      ))
    }
  </CategoryContainer>
);

const ArticleTags: React.FC<{ tags?: string[] }> = ({ tags }) => {
  if (!tags || tags.length === 0) return null;
  
  return (
    <CountriesContainer>
      {tags.map((tag, index) => (
        <CountryTag 
          key={index} 
          to={`/country/${tag}`}
        >
          {tag}
        </CountryTag>
      ))}
    </CountriesContainer>
  );
};

interface TocProps {
  article: Article;
  processedContent: string;
}

const TableOfContentsComponent: React.FC<TocProps> = ({ article, processedContent }) => {
  // 処理済みコンテンツから見出しを検索
  const h1Headings = processedContent.match(/<h1[^>]*>(.*?)<\/h1>/g);
  const h2Headings = processedContent.match(/<h2[^>]*>(.*?)<\/h2>/g);
  const h3Headings = processedContent.match(/<h3[^>]*>(.*?)<\/h3>/g);
  
  // 見出しが一つもない場合は目次を表示しない
  if (!h1Headings?.length && !h2Headings && !h3Headings) return null;
  
  // 記事タイトルをHTMLタグなしで取得（比較用）
  const titleText = stripHtmlTags(article.title);
  
  // タイトルでないh1見出しを抽出
  const nonTitleH1Headings = h1Headings?.filter(heading => {
    const headingText = heading.replace(/<[^>]*>/g, '').trim();
    return headingText !== titleText.trim();
  });
  
  // 見出しが一つもない場合は目次を表示しない
  if (!nonTitleH1Headings?.length && !h2Headings && !h3Headings) return null;

  return (
    <TableOfContents>
      <TocTitle>目次</TocTitle>
      <TocList>
        {nonTitleH1Headings?.map((heading, index) => {
          const headingText = heading.replace(/<[^>]*>/g, '');
          const id = `heading-h1-${index}`;
          
          // h2ヘッダーをこのh1の下に表示するためにフィルタリング
          const h1Pos = processedContent.indexOf(heading);
          const nextH1 = nonTitleH1Headings[index + 1];
          const nextH1Pos = nextH1 ? processedContent.indexOf(nextH1) : Infinity;
          
          const relatedH2s = h2Headings?.filter((h2) => {
            const h2Pos = processedContent.indexOf(h2);
            return h2Pos > h1Pos && h2Pos < nextH1Pos;
          });
          
          return (
            <li key={id}>
              <a href={`#${id}`} className="toc-h1-link">{headingText}</a>
              
              {/* このh1に関連するh2見出しを番号なしでインデントして表示 */}
              {relatedH2s && relatedH2s.length > 0 && (
                <TocH2List>
                  {relatedH2s.map((h2, h2Index) => {
                    const h2Text = h2.replace(/<[^>]*>/g, '');
                    const h2Id = `heading-h2-${h2Headings?.indexOf(h2) ?? 0}`;
                    
                    // h3ヘッダーをこのh2の下に表示するためにフィルタリング
                    const h2Pos = processedContent.indexOf(h2);
                    const nextH2 = relatedH2s[h2Index + 1];
                    const nextH2Pos = nextH2 ? processedContent.indexOf(nextH2) : nextH1Pos;
                    
                    const relatedH3s = h3Headings?.filter((h3) => {
                      const h3Pos = processedContent.indexOf(h3);
                      return h3Pos > h2Pos && h3Pos < nextH2Pos;
                    });
                    
                    return (
                      <div key={h2Id}>
                        <a href={`#${h2Id}`}>{h2Text}</a>
                        
                        {/* このh2に関連するh3見出しをさらにインデントして表示 */}
                        {relatedH3s && relatedH3s.length > 0 && (
                          <TocH3Container>
                            {relatedH3s.map((h3) => {
                              const h3Text = h3.replace(/<[^>]*>/g, '');
                              const h3Id = `heading-h3-${h3Headings?.indexOf(h3) ?? 0}`;
                              return (
                                <TocH3Item key={h3Id}>
                                  <a href={`#${h3Id}`}>{h3Text}</a>
                                </TocH3Item>
                              );
                            })}
                          </TocH3Container>
                        )}
                      </div>
                    );
                  })}
                </TocH2List>
              )}
            </li>
          );
        })}
        
        {/* h1がない場合は、関連するh1がないh2見出しを表示 */}
        {(!nonTitleH1Headings || nonTitleH1Headings.length === 0) && h2Headings?.map((heading, index) => {
          const text = heading.replace(/<[^>]*>/g, '');
          const id = `heading-h2-${index}`;
          
          // h3ヘッダーをこのh2の下に表示するためにフィルタリング
          const h2Pos = processedContent.indexOf(heading);
          const nextH2 = h2Headings[index + 1];
          const nextH2Pos = nextH2 ? processedContent.indexOf(nextH2) : Infinity;
          
          const relatedH3s = h3Headings?.filter((h3) => {
            const h3Pos = processedContent.indexOf(h3);
            return h3Pos > h2Pos && h3Pos < nextH2Pos;
          });
          
          return (
            <li key={id}>
              <a href={`#${id}`}>{text}</a>
              
              {/* このh2に関連するh3見出し */}
              {relatedH3s && relatedH3s.length > 0 && (
                <TocH3Container>
                  {relatedH3s.map((h3) => {
                    const h3Text = h3.replace(/<[^>]*>/g, '');
                    const h3Id = `heading-h3-${h3Headings?.indexOf(h3) ?? 0}`;
                    return (
                      <TocH3Item key={h3Id}>
                        <a href={`#${h3Id}`}>{h3Text}</a>
                      </TocH3Item>
                    );
                  })}
                </TocH3Container>
              )}
            </li>
          );
        })}
      </TocList>
    </TableOfContents>
  );
};

interface ArticleContentSplitterProps {
  article: Article;
  processedContent: string;
}

const ArticleContentSplitter: React.FC<ArticleContentSplitterProps> = ({ article, processedContent }) => {
  const { intro, main } = splitContent(processedContent, article);
  
  return (
    <>
      {intro && (
        <ArticleContent dangerouslySetInnerHTML={{ __html: intro }} />
      )}
      
      <TableOfContentsComponent 
        article={article} 
        processedContent={processedContent} 
      />
      
      <ArticleContent dangerouslySetInnerHTML={{ __html: main }} />
    </>
  );
};

// SEO関連のヘルパー関数
const getDescription = (article: Article): string => {
  if (!article?.content) return "アラヨットラベル - 旅行ブログ記事";
  
  // HTMLタグを除去
  const textContent = article.content.replace(/<[^>]*>/g, '');
  // 最初の100文字を抽出（または全文）
  return textContent.length > 150 
    ? textContent.substring(0, 150) + '...' 
    : textContent;
};

const getOgImage = (article: Article): string | undefined => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://arayotravel.com';
  
  // サムネイル画像があればそれを最優先で使用
  if (article?.thumbnail) {
    let imageUrl = article.thumbnail;
    
    // 画像URLが既に絶対URLの場合はそのまま返す
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // 相対パスを絶対URLに変換
    if (!imageUrl.startsWith('/')) {
      imageUrl = `/${imageUrl}`;
    }
    
    // cover.webpの場合はWebP形式が利用可能かを示すため、拡張子を含めて返す
    if (imageUrl.includes('cover.webp')) {
      // 本番環境ではWebP形式が自動的に配信される（nginx設定による）
      return `${siteUrl}${imageUrl}`;
    }
    
    return `${siteUrl}${imageUrl}`;
  }
  
  // 本文から最初の画像を探す
  if (article?.content) {
    const imgMatch = article.content.match(/<img[^>]+src="([^">]+)"/);
    if (imgMatch && imgMatch[1]) {
      let imgSrc = imgMatch[1];
      
      // 画像URLが既に絶対URLの場合はそのまま返す
      if (imgSrc.startsWith('http')) {
        return imgSrc;
      }
      
      // 相対パスを絶対URLに変換
      if (!imgSrc.startsWith('/')) {
        imgSrc = `/${imgSrc}`;
      }
      
      return `${siteUrl}${imgSrc}`;
    }
  }
  
  // デフォルトのOG画像を返す（サイトのロゴ）
  return `${siteUrl}/images/icon_yoko.jpg`;
};

// 序文と本文に分割する関数
const splitContent = (processedContent: string, article: Article | null) => {
  if (!processedContent) return { intro: '', main: '' };
  
  // タイトルと同一のh1は除外するため、記事タイトルを取得
  const titleText = article ? stripHtmlTags(article.title).trim() : '';
  
  // 最初のh1タグを探す（タイトルと同一のものは除く）
  let h1Matches = processedContent.match(/<h1[^>]*>(.*?)<\/h1>/g) || [];
  let nonTitleH1 = null;
  
  // h1タグを順に確認し、タイトルと一致しないものを探す
  for (const h1 of h1Matches) {
    const h1Text = h1.replace(/<[^>]*>/g, '').trim();
    if (h1Text !== titleText) {
      nonTitleH1 = h1;
      break;
    }
  }
  
  // 最初のh1タグ（タイトルと同一ではないもの）の位置
  const firstNonTitleH1Index = nonTitleH1 ? processedContent.indexOf(nonTitleH1) : -1;
  
  // 最初のh2タグの位置
  const firstH2Index = processedContent.indexOf('<h2');
  
  // 序文の終わりを決定
  let introEndIndex = -1;
  
  if (firstNonTitleH1Index !== -1) {
    // h1タグが見つかった場合はそこまでを序文とする
    introEndIndex = firstNonTitleH1Index;
  } else if (firstH2Index !== -1) {
    // h1タグがなくh2タグが見つかった場合はh2タグまでを序文とする
    introEndIndex = firstH2Index;
  }
  
  if (introEndIndex === -1) {
    // h1もh2も見つからない場合は全てを本文として扱う
    return { intro: '', main: processedContent };
  }
  
  return {
    intro: processedContent.substring(0, introEndIndex),
    main: processedContent.substring(introEndIndex)
  };
};

// おすすめ記事コンポーネント
interface RecommendedArticlesProps {
  currentArticle: Article;
  allArticles: Article[];
}

const RecommendedArticles: React.FC<RecommendedArticlesProps> = ({ currentArticle, allArticles }) => {
  const [recommendedArticles, setRecommendedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // 推奨記事を取得する関数をuseCallbackでメモ化
  const getRecommendedArticles = useCallback(() => {
    setLoading(true);
    
    // 現在の記事を除外
    const otherArticles = allArticles.filter(article => article.slug !== currentArticle.slug);
    
    // 同じサブカテゴリの記事を優先
    let recommended: Article[] = [];
    
    if (currentArticle.subCategory) {
      recommended = otherArticles.filter(article => 
        article.subCategory === currentArticle.subCategory
      );
    }
    
    // サブカテゴリで十分な記事が見つからない場合、同じカテゴリの記事を追加
    if (recommended.length < 4 && currentArticle.category) {
      const sameCategory = otherArticles.filter(article => 
        article.category === currentArticle.category && 
        article.subCategory !== currentArticle.subCategory
      );
      
      // 既に取得したサブカテゴリの記事と合わせて最大4件まで
      recommended = [...recommended, ...sameCategory].slice(0, 4);
    }
    
    // まだ足りない場合はランダムな記事を追加
    if (recommended.length < 4) {
      const remainingCount = 4 - recommended.length;
      const alreadySelected = new Set(recommended.map(a => a.slug));
      
      // 既に選択された記事を除外してランダムに追加
      const randomArticles = otherArticles
        .filter(article => !alreadySelected.has(article.slug))
        .sort(() => 0.5 - Math.random())
        .slice(0, remainingCount);
      
      recommended = [...recommended, ...randomArticles];
    }
    
    // 常に4件の記事を表示するため、多すぎる場合は切り詰める
    setRecommendedArticles(recommended.slice(0, 4));
    setLoading(false);
  }, [currentArticle.slug, currentArticle.category, currentArticle.subCategory, allArticles]);
  
  // コンポーネントのマウント時に一度だけ実行
  useEffect(() => {
    getRecommendedArticles();
  }, [getRecommendedArticles]);
  
  // Add onClick handler to scroll to top
  const handleRecommendedClick = () => {
    window.scrollTo(0, 0);
  };
  
  if (loading || recommendedArticles.length === 0) {
    return null;
  }
  
  return (
    <RecommendedSection>
      <RecommendedTitle>あなたへのおすすめ</RecommendedTitle>
      <RecommendedGrid>
        {recommendedArticles.map((article) => (
          <RecommendedCard 
            key={article.slug} 
            to={`/article/${article.slug}`}
            onClick={handleRecommendedClick}
          >
            <RecommendedImage src={article.thumbnail} />
            <RecommendedContent>
              <RecommendedArticleTitle>
                {stripHtmlTags(article.title)}
              </RecommendedArticleTitle>
            </RecommendedContent>
          </RecommendedCard>
        ))}
      </RecommendedGrid>
    </RecommendedSection>
  );
};

// メインコンポーネント
const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { articles, loading: contextLoading, error: contextError, loadArticleBySlug } = useArticles();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [processedContent, setProcessedContent] = useState<string>('');
  const navigate = useNavigate();
  
  // メモリ内キャッシュフック（デフォルトで10MB）
  const { setArticleCache, getArticleCache } = useArticleCache();
  
  // 記事データの取得
  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // まずキャッシュから取得を試みる
        const cachedData = getArticleCache(slug);
        if (cachedData) {
          // キャッシュヒット
          setArticle(cachedData.article);
          setProcessedContent(cachedData.processedContent);
          setLoading(false);
          return;
        }
        
        // 次に既存の記事リストから検索
        const existingArticle = articles.find(article => article.slug === slug);
        if (existingArticle && existingArticle.content) {
          setArticle(existingArticle);
          
          // 記事コンテンツを処理した後に、キャッシュと状態を更新
          const processed = processAndPrepareContent(existingArticle);
          setProcessedContent(processed);
          setArticleCache(slug, existingArticle, processed);
          
          setLoading(false);
          return;
        }
        
        // なければ非同期で読み込み
        const loadedArticle = await loadArticleBySlug(slug);
        
        if (loadedArticle) {
          setArticle(loadedArticle);
          
          // 記事コンテンツを処理した後に、キャッシュと状態を更新
          const processed = processAndPrepareContent(loadedArticle);
          setProcessedContent(processed);
          setArticleCache(slug, loadedArticle, processed);
          
        } else {
          setError('記事が見つかりませんでした');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : '記事の読み込み中にエラーが発生しました');
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [slug, articles, loadArticleBySlug]);
  
  // 記事コンテンツの処理を行う関数
  const processAndPrepareContent = (articleData: Article): string => {
    if (!articleData || !articleData.content) return '';
    
    // 記事の本文内のタイトルを削除
    let content = articleData.content;
    
    // 記事タイトルをHTMLタグなしで取得（比較用）
    const titleText = stripHtmlTags(articleData.title);
    
    // 正規表現で使用するために特殊文字をエスケープ
    const escapedTitleText = titleText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // h1タグを削除
    content = content.replace(/<h1[^>]*>.*?<\/h1>/, '');
    
    // 記事タイトルと同じ内容を持つh2タグがある場合も削除
    const titleRegex = new RegExp(`<h[1-2][^>]*>${escapedTitleText}<\/h[1-2]>`, 'i');
    content = content.replace(titleRegex, '');
    
    // 先頭の段落がタイトルと同じ内容を持つ場合も削除
    const firstPRegex = new RegExp(`^\\s*<p[^>]*>${escapedTitleText}<\/p>`, 'i');
    content = content.replace(firstPRegex, '');
    
    // リンクの置換、YouTubeリンクの処理、ヘッディングIDの付与、改行の処理を行う
    return processLineBreaks(addHeadingIds(processYoutubeLinks(processArticleLinks(content, articles))));
  };
  
  // 記事ロード時にアナリティクスを送信
  useEffect(() => {
    if (slug && article && article.title) {
      trackArticleView(slug, article.title);
    }
  }, [slug, article]);
  
  // ホームへ戻るボタンのハンドラー
  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate('/');
    window.scrollTo(0, 0);
  };
  
  // ローディング表示
  if (loading || contextLoading) {
    return (
      <PageWrapper>
        <Loading>
          <h2>記事を読み込み中...</h2>
        </Loading>
      </PageWrapper>
    );
  }
  
  // エラー表示
  if (error || contextError) {
    return (
      <PageWrapper>
        <ErrorMessage>
          {error || contextError}
        </ErrorMessage>
        <HomeButton to="/" onClick={handleHomeClick}>トップページに戻る</HomeButton>
      </PageWrapper>
    );
  }
  
  // 記事が見つからない場合
  if (!article) {
    return (
      <PageWrapper>
        <NotFound>
          <h2>記事が見つかりませんでした</h2>
          <p>お探しの記事は存在しないか、移動した可能性があります。</p>
          <HomeButton to="/" onClick={handleHomeClick}>トップページに戻る</HomeButton>
        </NotFound>
      </PageWrapper>
    );
  }
  
  // 記事表示
  return (
    <PageWrapper>
      <SEO 
        title={stripHtmlTags(article.title)}
        description={getDescription(article)}
        keywords={`${article.category || ''}, ${article.subCategory || ''}, 旅行, 観光, ${article.country || ''}`}
        ogImage={getOgImage(article)}
        ogType="article"
        canonical={`/article/${article.slug || ''}`}
        isArticle={true}
        publishDate={article.created_at || article.date}
        author="アラヨットラベル"
        categories={article.category ? [article.category] : []}
        tags={article.tags || []}
      />
      
      <ArticleContainer>
        <ArticleHeader>
          <ArticleDateDisplay date={article.date} created_at={article.created_at} />
          <ArticleTitle dangerouslySetInnerHTML={{ __html: article.title || '' }} />
          <ArticleMeta>
            <ArticleCategories 
              category={article.category} 
              subCategory={article.subCategory} 
            />
            <ArticleTags tags={article.tags} />
          </ArticleMeta>
        </ArticleHeader>
        
        {article.thumbnail && <ArticleImage src={article.thumbnail} />}
        
        <ArticleContentSplitter 
          article={article} 
          processedContent={processedContent} 
        />
      </ArticleContainer>
      
      {/* おすすめ記事セクションを追加 */}
      {!loading && article && articles.length > 0 && (
        <RecommendedArticles 
          currentArticle={article} 
          allArticles={articles} 
        />
      )}
      
      <MascotWrapper>
        <HomeButton to="/" onClick={handleHomeClick}>ホームへ戻る</HomeButton>
        <Mascot />
      </MascotWrapper>
    </PageWrapper>
  );
};

// 名前付きスタイル変更
const StyledArticleDate = styled.div`
  font-family: ${theme.fonts.body};
  color: ${theme.colors.lightText};
  font-size: ${theme.fontSizes.sm};
  margin-bottom: ${theme.space.sm};
`;

const StyledArticleCategory = styled(Link)`
  font-family: ${theme.fonts.body};
  background-color: #e3f2fd;
  color: #1976d2;
  padding: ${theme.space.xs} ${theme.space.sm};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.fontSizes.sm};
  display: inline-block;
  text-decoration: none;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #bbdefb;
    color: #1565c0;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const StyledArticleSubCategory = styled(StyledArticleCategory)`
  background-color: #f3e5f5;
  color: #7b1fa2;
  
  &:hover {
    background-color: #e1bee7;
    color: #6a1b9a;
  }
`;

// 新しく追加：おすすめ記事セクションのスタイル
const RecommendedSection = styled.div`
  max-width: 800px;
  margin: ${theme.space.xl} auto 0;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    max-width: 95%;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 100%;
    padding: 0 ${theme.space.xs};
  }
`;

const RecommendedTitle = styled.h3`
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes.xl};
  margin-bottom: ${theme.space.md};
  color: white;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.lg};
  }
`;

const RecommendedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.space.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.space.xs};
  }
`;

const RecommendedCard = styled(Link)`
  background-color: white;
  border-radius: ${theme.radii.md};
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  height: 100%;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    border-radius: ${theme.radii.sm};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const RecommendedImage = styled.div<{ src?: string }>`
  width: 100%;
  height: 0;
  padding-bottom: 56.25%;
  background-image: url(${props => props.src || '/images/default-thumb.jpg'});
  background-size: cover;
  background-position: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-bottom: 65%;
  }
`;

const RecommendedContent = styled.div`
  padding: ${theme.space.sm};
  flex: 1;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.space.xs};
  }
`;

const RecommendedArticleTitle = styled.h4`
  font-family: ${theme.fonts.heading};
  font-size: ${theme.fontSizes.md};
  margin-bottom: 0;
  color: ${theme.colors.text};
  line-height: 1.4;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.sm};
    margin-bottom: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export default ArticlePage; 